import React, { useEffect, useState } from 'react';
import {
  Grid,
  Stack,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import MainCard from 'ui-component/cards/MainCard';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import { get, patch } from '../../axios/apiService';
import toast from 'react-hot-toast';

const CalculatorsTable = () => {
  const [calculators, setCalculators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Edit modal state
  const [open, setOpen] = useState(false);
  const [selectedCalculator, setSelectedCalculator] = useState(null);
  const [editPlanType, setEditPlanType] = useState('0');

  // Fetch calculators
  const fetchCalculators = async () => {
    try {
      setLoading(true);
      const response = await get('/calculators');
      const data = response?.values || response || [];
      const formatted = data.map((item) => ({
        id: item.id || item.Id,
        calculatorName: item.calculatorName || item.CalculatorName || '',
        planType: item.planType || item.plan_Type || item.Plan_Type || 'Free',
        remark: item.remark || ''
      }));
      setCalculators(formatted);
    } catch (err) {
      console.error('Error fetching calculators:', err);
      setCalculators([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalculators();
  }, []);

  // Filter calculators by name or planType
  const filteredCalculators = calculators.filter(
    (calc) => calc.calculatorName.toLowerCase().includes(search.toLowerCase()) || calc.planType.toLowerCase().includes(search.toLowerCase())
  );

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  // Open edit modal
  const handleEdit = (row) => {
    setSelectedCalculator(row);
    setEditPlanType(row.planType.toLowerCase() === 'free' ? '0' : '1'); // 0 if Free else 1
    setOpen(true);
  };

  // Close modal
  const handleClose = () => {
    setOpen(false);
    setSelectedCalculator(null);
    setEditPlanType('0');
  };

  // Save changes
  const handleSave = async () => {
    if (!selectedCalculator?.id) return handleClose();
    setLoading(true);
    try {
      const payload = {
        planType: editPlanType // send 0 or 1 as string to backend
      };

      handleClose();
      await patch(`Calculators/${selectedCalculator.id}`, payload);
      toast.success('Calculator updated successfully');
      await fetchCalculators();
    } catch (err) {
      console.error('Update failed:', err);
      toast.error('Failed to update calculator');
    } finally {
      setLoading(false);
    }
  };

  // Action buttons in table
  const ActionButtons = (row) => (
    <Stack direction="row" spacing={1}>
      <IconButton color="primary" onClick={() => handleEdit(row)}>
        <EditIcon />
      </IconButton>
    </Stack>
  );

  // Table columns
  const columns = [
    { field: 'calculatorName', headerName: 'Name', flex: 1 },
    { field: 'planType', headerName: 'Plan Type', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params) => <ActionButtons {...params.row} />,
      align: 'right',
      width: 120,
      sortable: false,
      filterable: false
    }
  ];

  return (
    <>
      <MainCard title="Calculators List">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <FormControl sx={{ width: '25ch' }} size="small">
                <OutlinedInput
                  placeholder="Search"
                  value={search}
                  onChange={handleSearchChange}
                  endAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ height: 500, width: '100%' }}>
              {loading ? (
                <LoadingBackdrop open={loading} />
              ) : (
                <DataGrid
                  rows={filteredCalculators}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableRowSelectionOnClick
                  slots={{ noRowsOverlay: CustomNoRowsOverlay }}
                  autoHeight
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold'
                    }
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </MainCard>

      {/* Edit Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box className="modalBox">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>Edit Calculator</Typography>
            <IconButton onClick={handleClose} sx={{ color: 'red' }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TextField
            select
            fullWidth
            label="Plan Type"
            value={editPlanType}
            onChange={(e) => setEditPlanType(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          >
            <MenuItem value="0">Free</MenuItem>
            <MenuItem value="1">Paid</MenuItem>
          </TextField>

          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button onClick={handleClose} variant="contained" color="error" sx={{ mt: 2, mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default CalculatorsTable;
